import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, MenuItem, TextField, CircularProgress, Grid } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { debounce, getApiErrorMessage } from '../../utilities/helper';
import { getCitiesAPI, getLoggedInuserProfile, getStatesAPI, updateProfieApi } from '../../apis';
import { RootState } from '../../store';
import { showSnackbar } from '../../store/slices/common.slice';
import { APIResponse, User } from '../../utilities/types';
import CustomAlert from '../elements/custom-alert.element';
import { updateLoggedUser } from '../../store/slices/auth.slice';

// ✅ Fake API to validate GST
const validateGSTAPI = async (gstNumber: string) => {
  // simulate API delay
  // await new Promise((resolve) => setTimeout(resolve, 600));
  const isValid = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gstNumber);
  return isValid;
};

// ✅ Yup validation schema
const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  companyName: Yup.string(),
  gstNumber: Yup.string()
    .nullable()
    .test('gst-validation', 'Invalid GST Number', async (value) => {
      if (!value) return true; // optional
      const isValid = await validateGSTAPI(value);
      return isValid;
    }),
  address: Yup.string(),
  state: Yup.string(),
  city: Yup.string(),
});

const ProfileForm = () => {
  const dispatch = useDispatch();
  const { token } = useSelector((state: RootState) => state.auth);

  const [cities, setCities] = useState<any[]>([]);
  const [validatingGST, setValidatingGST] = useState(false);
  const [states, setStates] = useState<any[]>([]);
  const [initialValues, setInitialValues] = useState<User>({
    id: 0,
    email: '',
    name: '',
    company_name: '',
    gst_number: '',
    address: '',
    state_id: null,
    city_id: null,
  });
  const [saveApiError, setSaveApiError] = useState<string | null>(null);
  const [saveApiSuccess, setSaveApiSuccess] = useState<string | null>(null);

  const getCities = useCallback((state_id: number) => {
    getCitiesAPI({ filters: { state_id } })
      .then((response: any) => {
        if (response.status) {
          setCities(response?.data.items || []);
        }
      })
      .catch((error) => {
        console.log('===========Get cities error', error);
      });
  }, []);

  const getUserProfile = useCallback(async () => {
    try {
      if (!token) {
        return;
      }
      const response = (await getLoggedInuserProfile()) as APIResponse<User>;
      if (response.status) {
        setInitialValues(response.data);
        getCities(response.data.state_id);
      } else {
        throw new Error('Error in fetching logged in user detail');
      }
    } catch (err) {
      dispatch(showSnackbar({ message: getApiErrorMessage(err), severity: 'error' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ✅ Debounced GST validator (on type)
  const debouncedGSTCheck = debounce(async (value: string | number, setFieldError: any) => {
    if (!value) return;
    setValidatingGST(true);
    const isValid = await validateGSTAPI(value.toString());
    if (!isValid) setFieldError('gstNumber', 'Invalid GST Number');
    setValidatingGST(false);
  }, 800);

  const updateProfile = useCallback(
    async (values: Partial<User>) => {
      setSaveApiSuccess(null);
      setSaveApiError(null);
      try {
        const response = (await updateProfieApi(values)) as APIResponse<User>;
        if (response.status) {
          dispatch(updateLoggedUser(response.data));
          setInitialValues(response.data);
          setSaveApiSuccess('Profile updated successfully');
        }
      } catch (err) {
        setSaveApiError(getApiErrorMessage(err));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    getUserProfile();
    getStatesAPI({})
      .then((response: any) => {
        setStates(response?.data.items || []);
      })
      .catch((error) => {
        console.log('===========Get states error', error);
      });
  }, [getUserProfile]);

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={(values) => {
          console.log('Form Submitted ✅', values);
          updateProfile(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
          setFieldError,
          isValid,
          dirty,
        }) => (
          <Form>
            {(saveApiError || saveApiSuccess) && (
              <Box sx={{ my: 1 }}>
                {saveApiSuccess && <CustomAlert type="success">{saveApiSuccess}</CustomAlert>}
                {saveApiError && <CustomAlert type="error">{saveApiError}</CustomAlert>}
              </Box>
            )}
            <Field
              as={TextField}
              fullWidth
              name="name"
              label="Name *"
              margin="normal"
              value={values.name}
              onChange={handleChange}
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
            />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  fullWidth
                  name="company_name"
                  label="Company Name"
                  margin="normal"
                  value={values.company_name}
                  onChange={handleChange}
                  error={touched.company_name && Boolean(errors.company_name)}
                  helperText={touched.company_name && errors.company_name}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ position: 'relative' }}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="gst_number"
                    label="GST Number"
                    margin="normal"
                    value={values.gst_number}
                    onChange={(e: any) => {
                      handleChange(e);
                      debouncedGSTCheck(e.target.value, setFieldError);
                    }}
                    onBlur={async () => {
                      if (values.gst_number) {
                        setValidatingGST(true);
                        const isGstValid = await validateGSTAPI(values.gst_number);
                        if (!isGstValid) setFieldError('gst_number', 'Invalid GST Number');
                        setValidatingGST(false);
                      }
                    }}
                    error={touched.gst_number && Boolean(errors.gst_number)}
                    helperText={touched.gst_number && errors.gst_number}
                  />
                  {validatingGST && (
                    <CircularProgress
                      size={22}
                      sx={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>

            {/* Address */}
            <Field
              as={TextField}
              fullWidth
              name="address"
              label="Address"
              margin="normal"
              multiline
              rows={3}
              value={values.address}
              onChange={handleChange}
              error={touched.address && Boolean(errors.address)}
              helperText={touched.address && errors.address}
            />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  select
                  fullWidth
                  name="state_id"
                  label="State"
                  margin="normal"
                  value={values.state_id}
                  onChange={(e: any) => {
                    handleChange(e);
                    getCities(e.target.value);
                    setFieldValue('city_id', null); // reset city when state changes
                  }}
                >
                  <MenuItem value="">Select State</MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  select
                  fullWidth
                  name="city_id"
                  label="City"
                  margin="normal"
                  value={values.city_id}
                  onChange={handleChange}
                >
                  <MenuItem value="">Select City</MenuItem>
                  {cities.map((city, i) => (
                    <MenuItem key={i} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
            </Grid>
            {/* Submit */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 3 }}
              disabled={!isValid || !dirty || validatingGST}
            >
              Submit
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ProfileForm;
